import api from "../../../utils/api";
import { theme } from "../../../config/theme";

// ============================================================================
// Conexión con Claude — frontend service. Implements the guided flow to connect
// a Claude client (Desktop / Code) to THIS tenant's MCP server:
//
//   1. Consentimiento OAuth (PKCE) → pantalla de consentimiento de Coding Cloud
//   2. Configuración / deep-link copiable para añadir el MCP en Claude
//   3. Estado de conexión en vivo (se refresca al volver del consentimiento)
//
// Depends on OAuth (#AUTH) and connections (#CONN) on the backend. The service
// is written against that contract but degrades gracefully when the backend
// endpoints are not yet deployed: the OAuth launch + PKCE + MCP config/deep-link
// are derived client-side, and status reads fall back to a "desconectado"
// state instead of throwing, so the guided UI always renders.
//
//   • state  (authed): GET  /mcp/connection            → ConnectionState
//   • grant  (authed): POST /mcp/connection/grant       body { code, codeVerifier, state }
//   • revoke (authed): DELETE /mcp/connection
// ============================================================================

export type ConnectionStatus = "connected" | "disconnected" | "pending";

/** Where the MCP points to — shown to the user before confirming the grant. */
export interface McpTarget {
  tenant: string; // tenant id (VITE_THEME), e.g. "coding-cloud"
  tenantName: string; // human label, e.g. "Coding Cloud"
  environment: string; // "development" | "production"
  company: string | null; // active company name (multi-tenant scope)
  serverName: string; // MCP server name shown in Claude's config
  serverUrl: string; // MCP endpoint the client will talk to
}

export interface ConnectionState {
  status: ConnectionStatus;
  account: string | null; // the Claude account/email that completed the grant
  lastConnectedAt: string | null; // ISO timestamp
  target: McpTarget;
}

// --- Environment-derived config ---------------------------------------------

const ENV = (import.meta.env.VITE_ENVIRONMENT as string) || "development";
const TENANT = (import.meta.env.VITE_THEME as string) || "coding-cloud";

/**
 * MCP endpoint the Claude client connects to. Prefer an explicit
 * VITE_MCP_SERVER_URL; otherwise derive it from the Functions API host so dev
 * and prod stay in sync without extra config.
 */
function deriveServerUrl(): string {
  const explicit = import.meta.env.VITE_MCP_SERVER_URL as string | undefined;
  if (explicit) return explicit;
  const apiUrl = import.meta.env.VITE_FIREBASE_API_URL as string | undefined;
  if (apiUrl) {
    try {
      const u = new URL(apiUrl);
      return `${u.origin}/mcp`;
    } catch {
      /* fall through */
    }
  }
  return "https://mcp.codingcloud.online/mcp";
}

const SERVER_NAME = `${TENANT}-${ENV}`;

/** Build the MCP target purely from env + session — no backend required. */
export function getMcpTarget(): McpTarget {
  return {
    tenant: TENANT,
    tenantName: theme.name || TENANT,
    environment: ENV,
    company: localStorage.getItem("userCompanyName"),
    serverName: SERVER_NAME,
    serverUrl: deriveServerUrl(),
  };
}

// --- Copyable MCP configuration & deep-link ---------------------------------

/**
 * Ready-to-paste config for `claude_desktop_config.json` (and Claude Code /
 * Windsurf, which share the shape). Uses `mcp-remote` to reach the hosted,
 * OAuth-protected MCP endpoint.
 */
export function buildMcpConfigJson(target: McpTarget): string {
  return JSON.stringify(
    {
      mcpServers: {
        [target.serverName]: {
          command: "npx",
          args: ["-y", "mcp-remote", target.serverUrl],
        },
      },
    },
    null,
    2,
  );
}

/**
 * Optional deep-link to add the MCP to Claude in one click. Only returned when
 * a deep-link base is configured (VITE_CLAUDE_DEEPLINK_BASE); otherwise the
 * copyable config above is the path. Returns null when not configured.
 */
export function buildClaudeDeepLink(target: McpTarget): string | null {
  const base = import.meta.env.VITE_CLAUDE_DEEPLINK_BASE as string | undefined;
  if (!base) return null;
  const payload = {
    name: target.serverName,
    url: target.serverUrl,
    transport: "http",
  };
  const encoded = encodeURIComponent(btoa(JSON.stringify(payload)));
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}config=${encoded}`;
}

// --- OAuth (PKCE) ------------------------------------------------------------

const PKCE_VERIFIER_KEY = "claudeMcpPkceVerifier";
const PKCE_STATE_KEY = "claudeMcpOauthState";

function base64UrlEncode(bytes: Uint8Array): string {
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(length = 64): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes).slice(0, length);
}

async function sha256Challenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** The redirect Coding Cloud's consent screen returns to (this same section). */
export function getRedirectUri(): string {
  return `${window.location.origin}/crm/setting/claude`;
}

/**
 * Build the authorize URL (PKCE) and persist verifier + state so the grant can
 * be completed when the consent screen returns. Returns the URL to navigate to.
 */
export async function buildAuthorizeUrl(target: McpTarget): Promise<string> {
  const verifier = randomString(64);
  const state = randomString(32);
  const challenge = await sha256Challenge(verifier);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(PKCE_STATE_KEY, state);

  const authorizeBase =
    (import.meta.env.VITE_OAUTH_AUTHORIZE_URL as string | undefined) ||
    `${new URL(target.serverUrl).origin}/oauth/authorize`;

  const params = new URLSearchParams({
    response_type: "code",
    client_id: target.serverName,
    redirect_uri: getRedirectUri(),
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    scope: "mcp:read mcp:write",
  });
  return `${authorizeBase}?${params.toString()}`;
}

/** Read the consent-screen callback params off the current URL, if present. */
export function readOAuthCallback(): { code: string; state: string } | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  if (!code || !state) return null;
  return { code, state };
}

/** Validate the returned state against the one we persisted before redirecting. */
export function isStateValid(state: string): boolean {
  return sessionStorage.getItem(PKCE_STATE_KEY) === state;
}

/** Remove the PKCE artifacts + callback params once the grant is settled. */
export function clearOAuthArtifacts(): void {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
  const url = new URL(window.location.href);
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState({}, document.title, url.pathname + url.hash);
}

// --- Backend connection state ------------------------------------------------

function fallbackState(status: ConnectionStatus = "disconnected"): ConnectionState {
  return {
    status,
    account: null,
    lastConnectedAt: null,
    target: getMcpTarget(),
  };
}

/**
 * Current connection state. Never throws: if the backend (#CONN) isn't reachable
 * yet, returns a "desconectado" state with the env-derived target so the guided
 * flow still renders. Pass `pending` to reflect an in-progress grant.
 */
export async function fetchConnectionState(): Promise<ConnectionState> {
  try {
    const res = await api.get("/mcp/connection");
    const data = res.data?.data;
    if (data && data.status) {
      return { ...data, target: { ...getMcpTarget(), ...data.target } };
    }
    return fallbackState();
  } catch {
    return fallbackState();
  }
}

/**
 * Complete the OAuth grant by exchanging the authorization code (+ PKCE
 * verifier) for a stored connection. Returns the refreshed state. Surfaces
 * errors to the caller so the UI can show a clear failure.
 */
export async function completeGrant(
  code: string,
  state: string,
): Promise<ConnectionState> {
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY) || "";
  const res = await api.post("/mcp/connection/grant", {
    code,
    codeVerifier,
    state,
  });
  const data = res.data?.data;
  return data && data.status
    ? { ...data, target: { ...getMcpTarget(), ...data.target } }
    : fallbackState("connected");
}

/** Disconnect: revoke the stored grant for this tenant. */
export async function revokeConnection(): Promise<void> {
  await api.delete("/mcp/connection");
}
